import { SelectInput, NewModal, TextInput } from 'lib/components';
import React from 'react';

import { Gap } from 'lib/components/styles/layout';
import { theme } from 'lib/style';
import {
  FieldAttributes,
  Field,
  Formik,
  Form,
  FormikValues,
  FieldProps,
} from 'formik';
import styled from 'styled-components/macro';
import * as Yup from 'yup';
import { useAddCategoryMutation } from 'lib/api/guides/addGuideCategoryMutation';
import { Button } from 'react-covideo-common';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

type InputFieldProps = FieldAttributes<{ name: string }>;

const InputField = (props: InputFieldProps) => {
  return <Field {...props} />;
};

interface IProps {
  closeModalHandler: () => void;
}

const renameModalSchema = Yup.object({
  name: Yup.string().required(),
});

export const getCategoryPlatformOptions = (t: TFunction) => ({
  BOTH: { label: t('Both'), value: 'both' },
  DEKTOP: { label: t('Desktop'), value: 'desktop' },
  MOBILE: { label: t('Mobile'), value: 'mobile' },
});

const Label = styled.label`
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  color: ${theme.palette.gray80};
`;

export const AddCategoryModal = ({ closeModalHandler }: IProps) => {
  const { t } = useTranslation();

  const CategoryPlatformOptions = getCategoryPlatformOptions(t);

  const { mutateAsync, isLoading } = useAddCategoryMutation(closeModalHandler);

  const onSubmitHander = (values: FormikValues) => {
    mutateAsync({ name: values.name, platform: values.platform });
  };
  return (
    <NewModal headerText={t('Add new category')} closeModal={closeModalHandler}>
      <Formik
        initialValues={{
          name: '',
          platform: CategoryPlatformOptions.DEKTOP.value,
        }}
        onSubmit={onSubmitHander}
        validationSchema={renameModalSchema}
        initialErrors={{ name: t('Name is required') }}
      >
        {({ handleSubmit, errors }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <Gap>
                <Label htmlFor='name'>{t('Enter name for new category')}</Label>

                <InputField
                  width='100%'
                  placeholder=''
                  name={'name'}
                  as={TextInput}
                  type='text'
                  disabled={isLoading}
                />
              </Gap>

              <Gap m='10px 0 0 0'>
                <Label htmlFor='platform'>
                  {t('Select platform for the new category')}
                </Label>

                <Field name='platform' readonly={isLoading}>
                  {({ field }: FieldProps) => {
                    const { value, ...rest } = field;
                    return (
                      <SelectInput
                        {...rest}
                        defaultValue={value}
                        disabled={isLoading}
                      >
                        {Object.values(CategoryPlatformOptions).map(
                          (option, index) => (
                            <option key={index} value={option.value}>
                              {option.label}
                            </option>
                          )
                        )}
                      </SelectInput>
                    );
                  }}
                </Field>
              </Gap>

              <Gap
                m='32px 0 0 0'
                alignItems='center'
                justifyContent='flex-end'
                gap='12px'
              >
                <Button
                  disabled={isLoading}
                  onClick={async () => {
                    closeModalHandler();
                  }}
                  text={t('Cancel')}
                  variant='secondary'
                />
                <Button
                  type='submit'
                  text={t('Save')}
                  disabled={!!errors.name || isLoading}
                  variant='primary'
                />
              </Gap>
            </Form>
          );
        }}
      </Formik>
    </NewModal>
  );
};
